import { useLocalSearchParams } from "expo-router";
import { useMemo } from "react";

import { useAppStore } from "@hooks/useAppStore";
import type { Choice, UnifiedCitation } from "@memorang/types";

import { hasStaticFeedback } from "@memorang/helpers/feedback";
import { resolveMaxScore } from "@memorang/helpers/scoring/max-score";
import { isAiSubmittable } from "@memorang/helpers/session";
import { isPteFeedbackAgentItem } from "../constants/pte/feedback";
import useAIFeedbackStore from "./useAIFeedbackStore";
import useCurrentSession from "./useCurrentSession";
import useItemAnsweredState from "./useItemAnsweredState";
import useSessionStore from "./useSessionStore";

export const usePostAnswerState = () => {
	const { index } = useLocalSearchParams<{
		index: string;
	}>();

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const answers = useSessionStore((state) => state.answers);
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const attempts = useSessionStore((state) => state.attempts);
	const isStreaming = useAIFeedbackStore((store) => store.streaming);

	const { currentItem } = useCurrentSession();

	const { isAnswered, inReviewMode, isSummativeTest } =
		useItemAnsweredState(currentItem);

	const aiResponse = useAIFeedbackStore(
		(state) => state.aiResponse?.[currentItem.id!],
	);
	const aiFeedback = useAIFeedbackStore(
		(state) => state.aiResponse?.[currentItem.id!]?.explanation,
	);
	const aiTranscription = useAIFeedbackStore(
		(state) => state.aiResponse?.[currentItem.id!]?.transcription,
	);

	const sessionView = useAppStore((state) => state.sessionView);
	const showCitations = useAppStore((state) => state.app.citations?.visible);
	const showTextBasedCitationOnly = useAppStore((state) =>
		Boolean(state.app.citations?.showTextBasedCitationOnly),
	);
	const appFont = useAppStore((state) => state.app.font) ?? "opensans";
	const fontSize =
		useAppStore((state) => state.app.fontSizes?.explanation) || 14;
	const aiAssistant = useAppStore((state) => state.app.aiAssistant);
	const aiAssistantTitle = useAppStore((store) => store.app.aiAssistant.title);

	const { content, metadata } = currentItem;
	const choices: Choice[] =
		currentItem.response.type === "choice" ? currentItem.response.choices : [];
	const stem = content?.stem!;
	const type = metadata?.type!;

	const isAiItemType = isAiSubmittable(
		currentItem.response.type,
		currentItem.metadata?.type,
		currentItem.metadata?.toeic_artifact_type,
		hasStaticFeedback(currentItem.feedback),
	);
	const isAssessorItemType =
		isAiItemType && !isPteFeedbackAgentItem(currentItem.metadata?.type);
	const itemId = currentItem.id!;

	const topics = useMemo(
		() => currentItem.tags?.map((tag) => tag.label ?? "") || [],
		[currentItem.tags],
	);

	const currentAnswer = answers?.get(currentItem.id!) || currentItem.answer;

	const currentUserResponse = userResponse?.[itemId];

	const combinedCitations = useMemo((): UnifiedCitation[] => {
		return (
			metadata?.citations?.sort((a, b) => {
				if (a.url && !b.url) {
					return -1;
				}
				if (!a.url && b.url) {
					return 1;
				}
				return 0;
			}) ?? []
		);
	}, [metadata]);

	const trimmedUserResponse =
		typeof currentUserResponse === "string"
			? currentUserResponse.trim()
			: undefined;

	const numberOfWords =
		type === "ACADEMIC_DISCUSSION_RESPONSE" ||
		type === "INTEGRATED_WRITING_RESPONSE" ||
		type === "WRITING_EMAIL"
			? trimmedUserResponse
				? trimmedUserResponse.split(/\s+/).length
				: 0
			: 0;

	const currentAttempt = attempts.get(`${currentItem.id!}`);
	const attemptKeys = Object.keys(currentAttempt?.choiceAttempts || {});
	const numAttempts = attemptKeys.length;
	const isHintShown = useSessionStore((s) =>
		Boolean(s.itemStates?.get(itemId)?.hints?.length),
	);

	const showXpEarned = sessionView?.postAnswerStats?.showXpEarned;
	const showNumAttempts = sessionView?.postAnswerStats?.showNumAttempts;

	const lastAttemptChoiceId = attemptKeys[attemptKeys.length - 1];
	const numResponses = choices.find(
		(choice) => choice.id === lastAttemptChoiceId,
	)?.numFirstSelections;

	const shouldRenderPostAnswer = isAnswered;

	const maximumScore = resolveMaxScore(currentItem.scoring) ?? 0;

	const confidence = currentAnswer?.confidence;
	const correct = currentAnswer?.correct;
	const numTotalAttempts = choices.length || 0;
	const attemptsStatValue = `${numAttempts} of ${numTotalAttempts}`;

	return {
		aiAssistant,
		aiAssistantTitle,
		aiFeedback,
		aiResponse,
		aiTranscription,
		appFont,
		attemptKeys,
		attemptsStatValue,
		choices,
		combinedCitations,
		confidence,
		correct,
		currentAnswer,
		currentItem,
		currentItemIndex,
		currentUserResponse,
		fontSize,
		isAiItemType,
		isAssessorItemType,
		inReviewMode,
		isHintShown,
		isStreaming,
		isSummativeTest,
		itemId,
		maximumScore,
		metadata,
		numAttempts,
		numResponses,
		numberOfWords,
		shouldRenderPostAnswer,
		showCitations,
		showTextBasedCitationOnly,
		showNumAttempts,
		showXpEarned,
		stem,
		topics,
		type,
	};
};

export type PostAnswerState = ReturnType<typeof usePostAnswerState>;
